import React, {useMemo, useState} from "react";
import {useHistory} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";

import {notificationsActions} from "../store";
import { t } from "../common/LocalizationProvider";
import {formatDate} from "../common/formatter";
import ConfirmHandler from "./ConfirmHandler";

import Menu from "@material-ui/core/Menu";
import IconButton from "@material-ui/core/IconButton";
import {Delete, DeleteForeverOutlined, ExpandMore} from "@material-ui/icons";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import NotificationsIcon from "@material-ui/icons/Notifications";
import ListItemText from "@material-ui/core/ListItemText";
import {makeStyles} from "@material-ui/core/styles";
import {cyberStyles} from "../cssShared/cyberTheme";

const useStyles = makeStyles(theme => ({
    ...cyberStyles(theme),
    iconContainer: {
        minWidth: '40px',
        marginTop: '4px',
    },
    removeIconColor: {
        fontSize: '1.2rem',
        transition: 'color 0.2s',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '32px 16px',
        color: 'rgba(255, 255, 255, 0.4)',
        textAlign: 'center',
    },
}));

const NotificationsMenu = ({openNotsList, onClose, notifications}) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const classes = useStyles();
    const [openRemoveDialog, setOpenRemoveDialog] = useState(false);

    const sortedNotifications = useMemo(() => {
        return [...notifications].sort((a, b) => b.id - a.id);
    },[notifications])

    const language = useSelector(state => state.language.language);

    const handlUnseenChange = async (event, notification) => {
        try {
            if (notification.unseen){
                const response = await fetch('/api/nots',
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(notification)
                    })
                if (response.ok)
                    dispatch(notificationsActions.updateNotifications(await response.json()))
            }
        }catch (e) {
            console.error('Fetch error:', e.message);
        }
        if (notification.deviceid !== 0)
            history.push('/device/' + notification.deviceid);
        if(notification?.attributes?.title === "confirmEmail")
            history.push('/admin/users');
    }

    const handleRemove = async (notification) => {
        try{
            const response = await fetch('/api/nots/' + notification.id,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(notification)
                })
            dispatch(notificationsActions.removeNotifications(await response.json()))
        }catch (e) {
            console.error('Fetch error:', e.message);
        }
    }

    const handleGetMore = async () => {
        try{
            const response = await fetch(`/api/nots/notificationsWSMore/${sortedNotifications.length}`);
            const res = await response.json()
            dispatch(notificationsActions.updateNotifications(res));
        }catch (e) {
            console.error('Fetch error:', e.message);
        }
    }

    const handleCloseRemoveDialog = () => {
        setOpenRemoveDialog(false);
    }

    return (
        <div>
            <Menu
                anchorEl={openNotsList}
                open={Boolean(openNotsList)}
                onClose={onClose}
                PaperProps={{ className: classes.menuPaper }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                getContentAnchorEl={null}
            >
                <div className={classes.headerContainer}>
                    <div className={classes.removeAllButton} onClick={() => setOpenRemoveDialog(true)}>
                        <DeleteForeverOutlined className={classes.icons} />
                        <span className={classes.letters}>{t('RemoveAll')}</span>
                    </div>
                </div>

                {sortedNotifications.length > 0 ?(
                    sortedNotifications.map((notification) => (
                        <MenuItem
                            key={notification.id}
                            onClick={(event) => handlUnseenChange(event, notification)}
                            className={`${classes.menuItemNotifications} ${notification.unseen ? classes.notificationsList : ''}`}
                            disableGutters
                        >
                            <ListItemIcon className={classes.iconContainer}>
                                <NotificationsIcon className={classes.notificationIconColor}/>
                            </ListItemIcon>
                            <ListItemText
                                primaryTypographyProps={{ className: classes.titleText, variant: 'subtitle2' }}
                                primary={t(notification.attributes.title)}
                                secondary={
                                    <>
                                        {t(notification.attributes.message) && (
                                            <span className={classes.wrapping}
                                                  dir={language === 'ar' || language === 'fa' ? 'rtl' : 'ltr'}>{t(notification.attributes.message)
                                                .replace('%u', notification.attributes.senderName)
                                                .replace('%d', notification.attributes.deviceName)
                                            }</span>
                                        )}
                                        <span className={classes.dateText}>{formatDate(notification.created_at)}</span>
                                    </>
                                }
                            />
                            <IconButton
                                className={classes.removeIconBtn}
                                size="small"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleRemove(notification);
                                }}
                            >
                                <Delete className={classes.removeIconColor}/>
                            </IconButton>
                        </MenuItem>
                    ))
                ):(
                    <div className={classes.emptyState}>
                        <NotificationsIcon style={{ fontSize: 40, color: 'rgba(0, 242, 255, 0.2)', marginBottom: 10 }} />
                        <span className={classes.wrapping} dir={language === 'ar' || language === 'fa' ? 'rtl' : 'ltr'}>
                            {t('noNotificationMessage')}
                        </span>
                    </div>
                )}
                <div className={classes.moreButtonContainer}>
                    <div className={classes.moreButton} onClick={handleGetMore}>
                        <ExpandMore className={classes.icons} />
                        <span className={classes.letters}>{t('more')}</span>
                    </div>
                </div>

            </Menu>
            <ConfirmHandler open={openRemoveDialog} endpoint="notificationsWS" handleCloseDialog={handleCloseRemoveDialog} sortedNotifications={sortedNotifications} />
        </div>
    );
}
export default React.memo(NotificationsMenu);